import { IOrderInfo, ISymbol, ITicker, Market, OrderState } from './market-base.class';
import HmacSHA256                               from 'crypto-js/hmac-sha256';
import { IMarketSettingsToken } from './IMarketSettingsToken';

export class MarketBinanceGlobal extends Market<IMarketSettingsToken>
{
    protected name:              string      = 'Binance';
    protected icon:              string      = '/assets/images/exchanges/binance.svg';

    private accessUrl:           string      = 'https://api.binance.com';
    private currencies:          string[];
    private symbols:             ISymbol[];
    private prefferedCurrencies: string[]    = ['JASMY', 'ETH'];
    private balances:            Object      = {};
    private forceBalances:       boolean     = false;
    private proxyApi:            string      = '/api/market/binance-global';

    //#region Market abstract implementations

    public async checkConnection(): Promise<boolean>
    {
        let response;
        try{
            response = await this.requestAccount();
            if(typeof response['code'] === 'undefined'){
                return true;
            }

            throw new Error(`checkConnection failed: ${response['msg']}`);
        }
        catch(err){
            console.error(err);
            throw err;
        }
    }

    public async getCurrencies(): Promise<string[]>
    {
        if (!this.currencies){
            await this.initExchangeInfo();
        }
        return this.currencies;
    }

    public async getSymbols(): Promise<ISymbol[]>
    {
        if(!this.symbols){
            await this.initExchangeInfo();
        }
        return this.symbols;
    }

    public async getBalance(_currency: string): Promise<string>
    {
        if (!!this.balances[_currency] && !this.forceBalances) return this.balances[_currency];

        try {
            const response = await this.requestAccount();
            if (typeof response['code'] !== 'undefined')
            {
                throw new Error(`${response['msg']}`);
            }

            response['balances'].forEach((b) => (
                this.balances[b.asset.toLowerCase()] = b.free || '0.00')
            );

            if(!this.balances[_currency]) this.balances[_currency] = '0.00';
            return this.balances[_currency];
        }
        catch(err){
            console.error(`getBalance failed: ${err}`);
            throw err;
        }
    }

    public async getTicker(_symbol: string): Promise<ITicker>
    {
        try{
            const endpoint = `/api/v3/ticker/price`;

            const url      = `${this.accessUrl}${endpoint}`;
            const params = `symbol=${_symbol}`;

            const response = await this.http.post(this.proxyApi, {
                                                                url: `${url}?${params}`,
                                                                method: 'GET'
                                                            })
                                                            .toPromise();
            if (typeof response['code'] !== 'undefined')
            {
                throw new Error(`${response['msg']}`);
            }

            return {
                buy:  response['price'],
                sell: response['price']
            };
        }
        catch(err){
            console.error(`getTicker failed: ${err}`);
            throw err;
        }
    }

    public async getOrderInfo(_orderId: string, _symbol: string): Promise<IOrderInfo>
    {
        try{
            const endpoint = '/api/v3/order';

            const params = {
                orderId:    _orderId,
                symbol:     _symbol.toUpperCase(),
                timestamp:  Date.now()
            };

            const queryString = this.createQueryString(params);
            const sign = this.getQuerySignature(queryString);

            const url = `${this.accessUrl}${endpoint}?${queryString}&signature=${sign}`;
            const headers   = this.getHeaders();

            const response = <any>await this.http.post(this.proxyApi, {
                                                                 url,
                                                                 method: 'GET',
                                                                 headers
                                                             })
                                                             .toPromise();

            if (typeof response['code'] !== 'undefined')
            {
                throw new Error(`getOrderInfo failed: ${response['msg']}`);
            }

            return this.parseOrderInfo(response);
        }
        catch(err){
            console.error(err);
            throw err;
        }
    }

    public async getOrdersHistory(_symbol): Promise<IOrderInfo[]>
    {
        if (!_symbol) {
            return [];
        }

        try{
            const endpoint = '/api/v3/allOrders';

            let params = {
                symbol:    _symbol.toUpperCase(),
                timestamp: Date.now()
            };
            const queryString = this.createQueryString(params);
            const sign = this.getQuerySignature(queryString);

            const url = `${this.accessUrl}${endpoint}?${queryString}&signature=${sign}`;
            const headers   = this.getHeaders();

            const response: any = await this.http.post(this.proxyApi, {
                                                                 url,
                                                                 method: 'GET',
                                                                 headers
                                                             })
                                                             .toPromise();

            if (typeof response['code'] !== 'undefined')
            {
                throw new Error(`getOrdersHistory failed: ${response['msg']}`);
            }

            return response.map((o) => this.parseOrderInfo(o))
                           .sort((a, b) => b.finishedAt - a.finishedAt);
        }
        catch(err){
            console.error(err);
            throw err;
        }
    }

    public async createOrder(_symbol: string, _amount: string, _type: 'buy' | 'sell'): Promise<string>
    {
        try{
            const symbol = this.symbols.find(s => s.symbol == _symbol);
            const quantity = _type != 'buy' ? +_amount : await this.convertAmount(+_amount, symbol, _type);
            const endpoint      = `/api/v3/order`;
            const timestamp = Date.now();

            const params = {
                symbol:     _symbol.toUpperCase(),
                side:       _type.toUpperCase(),
                type:       'MARKET',
                quantity,
                timestamp
            };

            const queryString = this.createQueryString(params);
            const signature = this.getQuerySignature(queryString);

            const url = `${this.accessUrl}${endpoint}?${queryString}&signature=${signature}`;

            const headers   = this.getHeaders();

            const response = <any>await this.http.post(this.proxyApi, {
                                                                 url,
                                                                 method: 'POST',
                                                                 headers
                                                             })
                                                             .toPromise();

            if (typeof response['code'] !== 'undefined')
            {
                throw new Error(`createOrder failed: ${response['msg']}`);
            }

            this.balances = {};
            return response['orderId'];
        }
        catch(err){
            console.error(err);
            throw err;
        }
    }
    //#endregion

    //#region private methods

    private async initExchangeInfo(){
        try{
            const endpoint = '/api/v3/exchangeInfo';
            const url      = `${this.accessUrl}${endpoint}`;

            const response = await this.http.post(this.proxyApi, {
                                                                    url,
                                                                    method: 'GET'
                                                                }).toPromise();

            if (typeof response['code'] !== 'undefined')
            {
                throw new Error(`exchangeInfo failed: ${response['msg']}`);
            }

            const currenciesSet: Set<string> = new Set();
            const symbolsSet: Set<ISymbol> = new Set();

            for (const s of response['symbols']) {
                if(s.orderTypes.includes('MARKET')){
                    currenciesSet.add(s.baseAsset);
                    currenciesSet.add(s.quoteAsset);

                    if(s.symbol == 'ETHUSDT'){
                        console.debug(`${JSON.stringify(s)}`);
                    }

                    var smb = <ISymbol>{
                        symbol: s.symbol,
                        quote_currency: s.quoteAsset,
                        base_currency: s.baseAsset,
                        precision: s.baseAssetPrecision
                    };
                    const lotSizeFilter = s.filters.find(f => f.filterType == 'LOT_SIZE');
                    if(lotSizeFilter){
                        smb['stepSize'] = lotSizeFilter.stepSize;
                    }

                    symbolsSet.add(smb);
                }
            }

            this.currencies = this.sortCurrencies(Array.from(currenciesSet));
            this.symbols = Array.from(symbolsSet);
        }
        catch(err){
            console.error(`getCurrencies failed: ${err}`);
            throw err;
        }
    }

    private async requestAccount(){
        const endpoint = '/api/v3/account';
        const url      = `${this.accessUrl}${endpoint}`;

        const headers   = this.getHeaders();

        const nNonce = Date.now();
        const preSignedText = `timestamp=${nNonce}`;
        const sign = this.getQuerySignature(preSignedText);
        const params = `${preSignedText}&signature=${sign}`;

        const response = await this.http.post(this.proxyApi, {
                                                            url: `${url}?${params}`,
                                                            method: 'GET',
                                                            body: '',
                                                            headers
                                                         })
                                                         .toPromise();
        return response;
    }

    private parseOrderInfo(_orderInfo: any): IOrderInfo
    {
        const sendAmount =
            _orderInfo['side'] === 'BUY' ?
            _orderInfo['cummulativeQuoteQty'] :
            _orderInfo['origQty'];
        const getAmount =
            _orderInfo['side'] === 'BUY' ?
            _orderInfo['origQty'] :
            _orderInfo['cummulativeQuoteQty'];

        const currencies = this.coinsService.parseExchangeSymbol(_orderInfo['symbol']);
        const currencySend =_orderInfo['side'] === 'BUY' ? currencies[1] : currencies[0];
        const currencyGet = _orderInfo['side'] === 'BUY' ? currencies[0] : currencies[1];

        let price = _orderInfo['price'];
        if(typeof price !== 'undefined' && Number(price) == 0){
            price = +getAmount / +sendAmount;
        }
        const fee = undefined;

        return <IOrderInfo>{
            orderId:        _orderInfo['orderId'],
            currencySend,
            sendAmount,
            currencyGet,
            getAmount,
            fee,
            price,
            finishedAt:     _orderInfo['time'].toString(),
            state:          this.parseOrderState(_orderInfo['status']),
            type:           _orderInfo['side'].toLowerCase(),
            symbol:         _orderInfo['symbol']
        };
    }

    private parseOrderState(_orderStatus: string): OrderState {
        if (['NEW'].includes(_orderStatus)) {
          return 'processing';
        }

        if (['FILLED', 'PARTIALLY_FILLED'].includes(_orderStatus)) {
          return 'complete';
        }

        if (['CANCELED', 'PENDING_CANCEL'].includes(_orderStatus)) {
          return 'canceled';
        }

        return 'failed';
    }

    private createQueryString(_params: Object = {})
    {
        const result = [];

        for (let key in _params)
        {
            const encodedKey   = encodeURIComponent(key.replace(/[!'()*]/g, escape));
            const encodedValue = encodeURIComponent(_params[key].toString().replace(/[!'()*]/g, escape));

            result.push(`${encodedKey}=${encodedValue}`)
        }

        return result.join('&');
    }

    private getQuerySignature(_query: string)
    {
        const hash = HmacSHA256(_query, this.settings.secret_key).toString();
        return hash;
    }

    private getHeaders(): Object
    {
        return {
            'X-MBX-APIKEY' : this.settings.access_key,
        };
    }

    private sortCurrencies(_currencies: string[]): string[]
    {
        return _currencies.sort((a, b) =>
        {
            const indexA = this.prefferedCurrencies.indexOf(a);
            const indexB = this.prefferedCurrencies.indexOf(b);
            if (indexA === -1) return 0;

            if (indexA < indexB) return -1;

            if (indexA !== -1 && indexB === -1) return -1;

            if (indexA > indexB) return 1;

            return 0;
        });
    }

    //#endregion
}

